import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { FaHeart, FaCartPlus, FaArrowRight } from 'react-icons/fa';
import { CiStar } from 'react-icons/ci';
import toast from 'react-hot-toast';
import { cartProductCount } from '../../../redux/reduxApi/website/CartProductCount';
import { getWishlistCount } from '../../../redux/reduxApi/website/WishlistApi';
import { addToWishlist, deleteFromWishlist } from '../../../redux/api/website/AddtoWishlistApi';
import { AddToCart } from './utilsfuncart';
import 'swiper/css';
import 'swiper/css/navigation';

const RelatedProducts = React.memo(function RelatedProducts({
  relatedProducts,
  initialLikedPartKeys,
  productPageKey,
  onNavigateSinglePage,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [likedPartKeys, setLikedPartKeys] = useState(() => [
    ...(initialLikedPartKeys || []),
  ]);
  const likedPartKeysRef = useRef(likedPartKeys);
  likedPartKeysRef.current = likedPartKeys;

  useEffect(() => {
    setLikedPartKeys([...(initialLikedPartKeys || [])]);
  }, [productPageKey, initialLikedPartKeys]);

  const addToWishList = useCallback(
    async (product) => {
      const wishlistdata = {
        customerId: localStorage.getItem('customerId'),
        partKey: product.part_key,
        uniqueSerialNo: product.part_no + product.aaiabrandid,
        price: product.sellingPrice ?? 0,
        part_no: product.part_no,
        brand: product.brand || '',
        supplier: product.supplier || '',
        part_type: product.part_type || '',
        image: product.image || '',
      };

      try {
        const response = await dispatch(addToWishlist(wishlistdata));
        dispatch(getWishlistCount());
        if (response.payload?.data?.success) {
          toast.success('Product added to wishlist successfully');
        }
      } catch (error) {
        console.error('Error adding product to wishlist:', error);
      }
    },
    [dispatch]
  );

  const deleteFromWishList = useCallback(
    async (product) => {
      const payload = {
        customerId: localStorage.getItem('customerId'),
        partkey: product.part_key,
      };

      try {
        const response = await dispatch(deleteFromWishlist(payload));
        dispatch(getWishlistCount());
        if (response.payload?.data?.success) {
          toast.success('Product removed from wishlist successfully');
        }
      } catch (error) {
        console.error('Error removing product from wishlist:', error);
      }
    },
    [dispatch]
  );

  const toggleLike = useCallback(
    async (product, e) => {
      e?.stopPropagation?.();
      e?.preventDefault?.();

      if (!localStorage.getItem('customerId')) {
        localStorage.setItem('redirect', location.pathname);
        navigate('/login');
        return;
      }

      const pk = product.part_key;
      const wasLiked = likedPartKeysRef.current.includes(pk);

      setLikedPartKeys((prev) =>
        wasLiked ? prev.filter((id) => id !== pk) : [...prev, pk]
      );

      if (!wasLiked) {
        await addToWishList(product);
      } else {
        await deleteFromWishList(product);
      }
    },
    [location.pathname, navigate, addToWishList, deleteFromWishList]
  );

  const handleAddRelatedToCart = useCallback(
    async (item, e) => {
      e?.stopPropagation?.();
      e?.preventDefault?.();

      if (!localStorage.getItem('customerId')) {
        localStorage.setItem('redirect', location.pathname);
        navigate('/login');
        return;
      }

      try {
        const payload = {
          ...item,
          uniqueKey: item.part_no + item.aaiabrandid,
        };
        await AddToCart(payload, 1);
        dispatch(cartProductCount(localStorage.getItem('customerId')));
      } catch (error) {
        console.error('Error adding related product to cart:', error);
      }
    },
    [dispatch, location.pathname, navigate]
  );

  if (!relatedProducts?.length) return null;

  return (
    <div className="w-11/12 mx-auto">
      <h1 className="lg:text-4xl font-bold text-blue-900 dark:text-black-700 md:text-lg text-center pb-3">
        Similar Products
      </h1>
      <hr className="heading-seperator border-4 border-orange-600" />
      <div className="relative group">
        <Swiper
          modules={[Navigation]}
          spaceBetween={20}
          slidesPerView={'auto'}
          navigation={{
            nextEl: '.related-next',
            prevEl: '.related-prev',
          }}
          breakpoints={{
            480: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 4 },
            1280: { slidesPerView: 5 },
          }}
          className="!pb-8"
          key={`swiper-${relatedProducts.length}`}
        >
          {relatedProducts.map((item) => (
            <SwiperSlide key={item.part_key} className="h-auto" style={{ marginRight: '10px' }}>
              <div className="product-card border-2 rounded-lg mt-3 hover:border-blue-400 hover:scale-105 transition-all duration-300 ease-in-out flex flex-col bg-white">
                <div className="flex justify-between items-start">
                  <div className="flex space-x-1">
                    {item.tags?.map((tag) => (
                      <span
                        key={tag}
                        className={`text-xs font-bold px-2 py-1 rounded ${
                          tag === 'Sale'
                            ? 'bg-yellow-500 text-white'
                            : tag === 'Popular'
                              ? 'bg-blue-900 text-white'
                              : 'bg-green-500 text-white'
                        }`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <FaHeart
                    size="25px"
                    className={`heart-icon cursor-pointer z-1 stroke-2 ${
                      likedPartKeys.includes(item.part_key)
                        ? 'text-orange-500 fill-orange-500'
                        : 'text-blue fill-blue'
                    }`}
                    onClick={(e) => toggleLike(item, e)}
                  />
                </div>
                <img
                  src={item.image}
                  alt={item.part_type}
                  loading="lazy"
                  onClick={() => onNavigateSinglePage(item)}
                  className="w-full h-[25vh] object-contain p-1 hover:scale-90 transition-all duration-300 ease-in-out cursor-pointer rounded-t-md"
                />
                <p
                  className="font-semibold px-2 lg:text-base cursor-pointer text-2xl text-start overflow-hidden mb-0"
                  onClick={() => onNavigateSinglePage(item)}
                >
                  {item.part_type}
                </p>
                <p className="font-normal px-2 lg:text-base cursor-pointer text-2xl text-start overflow-hidden mb-0">
                  {item.part_no}
                </p>
                <div className="flex items-center justify-between m-2 space-y-0">
                  <div className="text-red-600 font-bold">
                    {item.sellingPrice === 'Loading...'
                      ? 'Loading...'
                      : `$${item.sellingPrice}`}
                  </div>
                </div>
                <div className="flex items-center my-2 mx-2">
                  <div className="flex space-x-1 -mt-3">
                    {[...Array(5)].map((_, i) => (
                      <CiStar key={i} className="text-gray-400" />
                    ))}
                  </div>
                </div>
                <div className="flex justify-start items-center">
                  <button
                    type="button"
                    onClick={(e) => handleAddRelatedToCart(item, e)}
                    className="text-white bg-blue-900 p-2 rounded-lg font-bold flex mt-1 items-center mx-2 mb-2 text-sm hover:scale-90 transition-all duration-300 ease-in-out"
                  >
                    <FaCartPlus size={'20px'} className="ms-0 me-2" />
                    Add To Cart
                    <FaArrowRight size={'18px'} className="mx-2" />
                  </button>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <button
          type="button"
          className="related-prev absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white shadow-lg rounded-full flex items-center justify-center text-blue-600 hover:bg-gray-50 transition-colors -translate-x-1/2"
          style={{ zIndex: 1 }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          type="button"
          className="related-next absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white shadow-lg rounded-full flex items-center justify-center text-blue-600 hover:bg-gray-50 transition-colors translate-x-1/2"
          style={{ zIndex: 1 }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
});

RelatedProducts.displayName = 'RelatedProducts';

export default RelatedProducts;
